import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

function activityLogTimeMs(a: ActivityLog): number {
  const t = a.timestamp;
  if (typeof t === 'number' && !Number.isNaN(t) && t > 0) return t;
  if (a.createdAt) {
    const ms = new Date(a.createdAt).getTime();
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return 0;
}

/** Newest first for workspace + per-member views that filter this list. */
export function sortActivityLogsNewestFirst(logs: ActivityLog[]): ActivityLog[] {
  return [...logs].sort((a, b) => activityLogTimeMs(b) - activityLogTimeMs(a));
}

/** .NET API: GET /workspaces/{id}/activity-logs → { logs, total, limit, offset } */
export interface BackendActivityLogsResponse {
  logs?: BackendActivityLogRow[];
  total?: number;
}

interface BackendActivityLogRow {
  id: string;
  tenantId: string;
  workspaceId: string;
  userId?: string;
  userName?: string;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  properties?: Record<string, unknown> | null;
  createdAt?: string;
  /** If JSON ever uses PascalCase */
  CreatedAt?: string;
}

/** Milliseconds from API created-at field(s). Never use Date.now() — that collapses all rows into "today". */
function createdAtToMs(row: BackendActivityLogRow): number {
  const raw = row.createdAt ?? row.CreatedAt;
  if (raw == null || raw === '') return NaN;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

/** Optional client event time from SDK `properties.timestamp` (seconds or ms). */
function timestampFromProperties(props: Record<string, unknown>): number | null {
  const t = props.timestamp;
  if (typeof t !== 'number' || !Number.isFinite(t)) return null;
  return t < 1e12 ? t * 1000 : t;
}

export function mapRowToActivityLog(row: BackendActivityLogRow): ActivityLog {
  const props =
    row.properties && typeof row.properties === 'object' && !Array.isArray(row.properties)
      ? { ...row.properties }
      : {};
  const emailFromProps = typeof props.email === 'string' ? props.email : '';
  const fromCreated = createdAtToMs(row);
  const fromProps = timestampFromProperties(props as Record<string, unknown>);
  const ts =
    fromProps != null && Number.isFinite(fromProps) && fromProps > 0
      ? fromProps
      : Number.isFinite(fromCreated) && fromCreated > 0
        ? fromCreated
        : NaN;
  const data = props as ActivityLog['data'];
  const createdAtIso =
    row.createdAt ??
    row.CreatedAt ??
    (Number.isFinite(fromCreated) && fromCreated > 0
      ? new Date(fromCreated).toISOString()
      : Number.isFinite(ts) && ts > 0
        ? new Date(ts).toISOString()
        : '');

  return {
    id: String(row.id),
    eventType: row.action ?? '',
    createdAt: createdAtIso,
    createdBy: row.userId ?? '',
    createdByName: row.userName ?? '',
    lastModifiedAt: createdAtIso,
    lastModifiedBy: '',
    lastModifiedByName: '',
    timestamp: Number.isFinite(ts) ? ts : 0,
    entityType: row.entityType ?? '',
    tenantId: String(row.tenantId),
    workspaceId: String(row.workspaceId),
    userEmail: emailFromProps || row.userName || '',
    data: data ?? {},
    SK: '',
    PK: '',
  };
}

export interface ActivityLogsWorkspacePage {
  logs: ActivityLog[];
  total: number;
}

async function getActivityLogsByWorkspace(
  token: string | undefined,
  workspaceId: string,
  pageIndex: number,
  pageSize: number,
): Promise<ActivityLogsWorkspacePage> {
  const limit = Math.min(Math.max(pageSize, 1), 500);
  const offset = pageIndex * limit;
  const response = await axios.get<BackendActivityLogsResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/activity-logs`,
    {
      headers: backendRequestHeaders(token),
      params: { limit, offset },
    },
  );
  const rows = response.data.logs ?? [];
  const total = response.data.total ?? 0;
  return {
    logs: sortActivityLogsNewestFirst(rows.map(mapRowToActivityLog)),
    total,
  };
}

export function useGetActivityLogsByWorkspace(pagination?: { pageIndex: number; pageSize: number }) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const pageIndex = pagination?.pageIndex ?? 0;
  const pageSize = pagination?.pageSize ?? 15;
  return useQuery<ActivityLogsWorkspacePage>({
    queryKey: [queryKeys.activityLogs, workspace?.id, pageIndex, pageSize],
    queryFn: () =>
      getActivityLogsByWorkspace(user?.signInUserSession.idToken.jwtToken, workspace?.id ?? '', pageIndex, pageSize),
    enabled: !!workspace?.id && !!user?.signInUserSession?.idToken.jwtToken,
    staleTime: 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}
